import type { Skill } from "../engine/types"

/**
 * Demo skills taxonomy — a plausible Swiss-retail-bank shape.
 *
 * SYNTHETIC. The real taxonomy would come from the SuccessFactors Attributes
 * Library (Talent Intelligence Hub) or be built with L&D; see the plan's
 * "Honest framing" section.
 */
export const SKILLS: Skill[] = [
    // — Regulatorik ---------------------------------------------------------
    { id: "reg.fidleg", label: "FIDLEG / Verhaltensregeln", category: "regulatorik" },
    { id: "reg.suitability", label: "Angemessenheits- & Eignungsprüfung", category: "regulatorik" },
    { id: "reg.gwg", label: "Geldwäschereiprävention (GwG)", category: "regulatorik" },
    { id: "reg.vsb", label: "Sorgfaltspflichten (VSB)", category: "regulatorik" },
    { id: "reg.kyc", label: "Know Your Customer", category: "regulatorik" },
    { id: "reg.datenschutz", label: "Datenschutz (DSG)", category: "regulatorik" },
    { id: "reg.bankkundengeheimnis", label: "Bankkundengeheimnis", category: "regulatorik" },
    { id: "reg.sanktionen", label: "Sanktionen & Embargos", category: "regulatorik" },
    { id: "reg.marktverhalten", label: "Marktverhaltensregeln", category: "regulatorik" },
    { id: "reg.opRisk", label: "Operationelles Risiko", category: "regulatorik" },
    { id: "reg.infosec", label: "Informationssicherheit", category: "regulatorik" },
    { id: "reg.bcm", label: "Business Continuity Management", category: "regulatorik" },
    { id: "reg.outsourcing", label: "Auslagerungen (FINMA-RS 18/3)", category: "regulatorik" },

    // — Fach ----------------------------------------------------------------
    { id: "fach.anlageberatung", label: "Anlageberatung", category: "fach" },
    { id: "fach.portfolio", label: "Portfoliokonstruktion", category: "fach" },
    { id: "fach.wertschriften", label: "Wertschriften & Instrumente", category: "fach" },
    { id: "fach.vorsorge", label: "Vorsorge (Säule 2/3)", category: "fach" },
    { id: "fach.steuern", label: "Steuern für Privatkunden", category: "fach" },
    { id: "fach.hypotheken", label: "Hypothekargeschäft", category: "fach" },
    { id: "fach.tragbarkeit", label: "Tragbarkeit & Belehnung", category: "fach" },
    { id: "fach.kreditanalyse", label: "Kreditanalyse", category: "fach" },
    { id: "fach.kreditrisiko", label: "Kreditrisikobeurteilung", category: "fach" },
    { id: "fach.firmenkunden", label: "Firmenkundengeschäft", category: "fach" },
    { id: "fach.bilanzanalyse", label: "Bilanz- & Jahresabschlussanalyse", category: "fach" },
    { id: "fach.esg", label: "Nachhaltigkeit & ESG", category: "fach" },
    { id: "fach.esgBeratung", label: "ESG in der Kundenberatung", category: "fach" },
    { id: "fach.zahlungsverkehr", label: "Zahlungsverkehr", category: "fach" },
    { id: "fach.produktkenntnis", label: "Produktkenntnis Acme Bank", category: "fach" },
    { id: "fach.beratungsprozess", label: "Beratungsprozess", category: "fach" },
    { id: "fach.gespraechsfuehrung", label: "Gesprächsführung mit Kunden", category: "fach" },
    { id: "fach.einwandbehandlung", label: "Einwandbehandlung", category: "fach" },
    { id: "fach.finanzplanung", label: "Ganzheitliche Finanzplanung", category: "fach" },
    { id: "fach.immobilienbewertung", label: "Immobilienbewertung", category: "fach" },
    { id: "fach.recovery", label: "Sanierung & Recovery", category: "fach" },

    // — Führung -------------------------------------------------------------
    { id: "fue.feedback", label: "Feedback geben & nehmen", category: "fuehrung" },
    { id: "fue.mag", label: "Mitarbeitergespräche führen", category: "fuehrung" },
    { id: "fue.konflikt", label: "Konfliktmanagement", category: "fuehrung" },
    { id: "fue.delegation", label: "Delegation & Priorisierung", category: "fuehrung" },
    { id: "fue.rekrutierung", label: "Rekrutierung & Auswahl", category: "fuehrung" },
    { id: "fue.teamentwicklung", label: "Teamentwicklung", category: "fuehrung" },
    { id: "fue.changeManagement", label: "Change Management", category: "fuehrung" },
    { id: "fue.arbeitsrecht", label: "Arbeitsrecht für Führungskräfte", category: "fuehrung" },
    { id: "fue.absenzen", label: "Absenz- & Case Management", category: "fuehrung" },

    // — Digital -------------------------------------------------------------
    { id: "dig.aiGrundlagen", label: "AI-Grundlagen", category: "digital" },
    { id: "dig.aiRisiken", label: "Risiken generativer AI", category: "digital" },
    { id: "dig.promptEngineering", label: "Prompt Engineering", category: "digital" },
    { id: "dig.datenanalyse", label: "Datenanalyse", category: "digital" },
    { id: "dig.excel", label: "Excel für Analysen", category: "digital" },
    { id: "dig.digitaleKanaele", label: "Digitale Kundenkanäle", category: "digital" },
    { id: "dig.cloudGrundlagen", label: "Cloud-Grundlagen", category: "digital" },
    { id: "dig.itArchitektur", label: "IT-Architektur", category: "digital" },
    { id: "dig.softwareEngineering", label: "Software Engineering", category: "digital" },
    { id: "dig.securityEngineering", label: "Security Engineering", category: "digital" },

    // — Methodik ------------------------------------------------------------
    { id: "met.projektmanagement", label: "Projektmanagement", category: "methodik" },
    { id: "met.agile", label: "Agile Arbeitsweisen", category: "methodik" },
    { id: "met.prozessmanagement", label: "Prozessmanagement", category: "methodik" },
    { id: "met.praesentation", label: "Präsentation & Moderation", category: "methodik" },
    { id: "met.verhandlung", label: "Verhandlungsführung", category: "methodik" },
    { id: "met.schreiben", label: "Schriftliche Kommunikation", category: "methodik" },
    { id: "met.selbstorganisation", label: "Selbstorganisation", category: "methodik" },
]

export const SKILL_BY_ID: ReadonlyMap<string, Skill> = new Map(SKILLS.map((s) => [s.id, s]))
